import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

/** Имя доски: непустое после trim, иначе в меню появляется пустой пункт. */
const BoardNameSchema = z
  .string()
  .trim()
  .min(1, 'Введите название')
  .max(80, 'Не больше 80 символов');

/**
 * Доска проекта (docs/05 §7). Тип (kanban/scrum) живёт у проекта, поэтому
 * доска — только имя, порядок и набор отображаемых компонентов: она задаёт
 * представление, а не хранит задачи.
 */
export const BoardConfigSchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  name: z.string(),
  position: z.number().int(),
  isDefault: z.boolean(),
  /** Workflow, колонки которого показывает доска (docs/03 §1). */
  workflowId: UuidSchema,
  /**
   * Компоненты, которые показывает доска (docs/13 §7). Пустой список —
   * «все компоненты проекта»: именно это состояние у новой доски, и оно
   * честно охватывает компоненты, созданные позже.
   */
  componentIds: z.array(UuidSchema),
  createdAt: IsoDateTimeSchema,
});
export type BoardConfig = z.infer<typeof BoardConfigSchema>;

/** Набор компонентов доски: без повторов, максимум 50 — как у проекта. */
const BoardComponentIdsSchema = z
  .array(UuidSchema)
  .max(50, 'Не больше 50 компонентов')
  .refine((ids) => new Set(ids).size === ids.length, 'Компоненты не должны повторяться');

export const CreateBoardBodySchema = z
  .object({
    name: BoardNameSchema,
    componentIds: BoardComponentIdsSchema.optional(),
    /** Workflow доски; без него — workflow по умолчанию (docs/05 §7). */
    workflowId: UuidSchema.optional(),
  })
  .strict();
export type CreateBoardBody = z.infer<typeof CreateBoardBodySchema>;

/**
 * Изменение доски: хотя бы одно поле. Раньше здесь было только имя, но набор
 * компонентов — такое же свойство доски, отдельного маршрута не требуется.
 */
export const UpdateBoardBodySchema = z
  .object({
    name: BoardNameSchema.optional(),
    componentIds: BoardComponentIdsSchema.optional(),
    /** Смена workflow доски: колонки меняются на его статусы (docs/05 §7). */
    workflowId: UuidSchema.optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.name !== undefined || body.componentIds !== undefined || body.workflowId !== undefined,
    'Нужно хотя бы одно поле: name, componentIds или workflowId',
  );
export type UpdateBoardBody = z.infer<typeof UpdateBoardBodySchema>;

export const ReorderBoardsBodySchema = z
  .object({ ids: z.array(UuidSchema).min(1, 'Нужна хотя бы одна доска') })
  .strict();
export type ReorderBoardsBody = z.infer<typeof ReorderBoardsBodySchema>;